import { makeAutoObservable } from 'mobx'
import { injectable, decorate, inject } from '@evertel/di'
import { NormalizedMedia } from '@evertel/media'
import { formatBytes, getFileAttributes, getFileExt } from '@evertel/utils'
import { Api } from '@evertel/api'
import { RoomStore } from '@evertel/stores'
import { DocumentStore } from '@evertel/document'


class MediaController {
    media: NormalizedMedia = {} as NormalizedMedia
    width = 400
    height = 300

    constructor(
        private api: Api,
        private roomStore: RoomStore,
        private documentStore: DocumentStore

    ) {
        makeAutoObservable(this)
    }

    init = async (media: NormalizedMedia, width?: number, height?: number) => {
        this.width = width
        this.height = height
        this.media = media
    }

    updateDimensions (width: number, height: number) {
        this.width = width
        this.height = height
    }

    resolveVersion = (): number => {
        if (!this.media?.versions?.length || !this.width || !this.height) return undefined

        const versions = this.media?.versions
        const area = this.width * this.height

        // select last version by default (original)
        let selected = versions.length - 1

        // remove last one from search
        const searchVersions = versions.slice(0, -1)

        let i = searchVersions.length - 1

        while (i >= 0) {
            const version = searchVersions[i]

            // smaller size version found?
            if (version.width * version.height < area) {
                break
            }

            // select smaller but fit size version
            selected = i

            i--
        }

        // If the selected version is far too small for the requested size,
        // return undefined to signal "use the original source"
        const selectedVersion = versions[selected]
        const selectedArea = selectedVersion?.width * selectedVersion?.height

        if (selectedArea < area * 0.25) {
            return undefined
        }

        return selected // index of closest version
    }

    get isLocal() {
        return this.media.isLocalMedia
    }

    get url() {
        // if already a blob or not an image, simply return the url
        if (this.media.url?.includes('blob') || this.media.mediaType !== 'image') return this.media.url

        // images only:
        // grab the closest optimized version based on width/height
        const versionIndex = this.resolveVersion()
        const version = (versionIndex !== undefined) ? this.media.versions[versionIndex].id : 'optimized'

        return `${this.media.url}?ver=${version}`
    }

    getDownloadContext = () => {
        const pathname = window.location.pathname.substring(1)
        const searchParams = new URLSearchParams(window.location.search)

        const selectedDoc = searchParams.get('selectedDoc') || (pathname.startsWith('everdocs/view/') ? pathname.split('/')[2] : null)

        if (selectedDoc) {
            const document = this.documentStore.findById(parseInt(selectedDoc))

            return {
                from: 'document',
                fromId: selectedDoc || 0,
                departmentId: document?.departmentId || 0
            }
        }

        if (pathname.startsWith('room/')) {
            const roomId = pathname.split('/')[1]
            const room = this.roomStore.findById(parseInt(roomId))

            return {
                from: 'room',
                fromId: roomId || 0,
                departmentId: room?.departmentId || 0
            }
        }

        return { from: 'unknown', fromId: 0, departmentId: 0 }

    }

    executeDownload = (media: NormalizedMedia, downloadUrl: string, target: string) => {
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.download = media.fileName
        a.href = downloadUrl
        a.target = target
        a.click()
    }

    downloadMedia = async (media: NormalizedMedia, target: '_blank'|'_self'|'_parent'|'_top' = '_blank') => {
        const {from, fromId, departmentId} = this.getDownloadContext()

        const data = {
            failed: from === 'unknown',
            roomId: from === 'room' ? String(fromId) : null,
            documentId: from === 'document' ? String(fromId) : null,
            mediaId: String(media.id)
        }

        let downloadUrl = media.url

        if (!media.url?.includes('blob') && media.mediaType === 'image') {
            downloadUrl = `${media.url}?ver=original`
        }

        if (!data.failed) {
            await this.api.Routes.Department.postHistoryMediaDownloaded(departmentId, data)
        }

        this.executeDownload(media, downloadUrl, target)
    }

    // To get the full original download for images
    get downloadUrl() {
        if (this.media.url?.includes('blob') || this.media.mediaType !== 'image') return this.media.url
        return `${this.media.url}?ver=original`
    }

    get fileExtension() {
        return getFileExt(this.media)
    }

    get humanizedFileSize() {
        return formatBytes(this.media?.contentLength, 1)
    }

    get hasPreview() {
        // used for videos
        const noPreviewPlatforms = ['mixcloud', 'player.vimeo.com/external', 'soundcloud', 'twitch']
        return (this.media?.id < 0) && !noPreviewPlatforms.some(el => this.media.url.includes(el))
    }

    get videoThumbnailUrl(): string | undefined {
        if (this.media?.mediaType !== 'video') return undefined
        if (!this.media?.versions?.length) return undefined

        const thumbnailVersion = this.media.versions.find(v => v.id === 'thumbnail')
        if (!thumbnailVersion) return undefined

        return `${this.media.url}?ver=thumbnail`
    }

    get videoDuration(): number | undefined {
        if (this.media?.mediaType !== 'video') return undefined
        return this.media?.duration
    }

    get fileTypeAttributes() {
        // used for application files
        return getFileAttributes(this.media.fileName)
    }
}

decorate(injectable(), MediaController)
decorate(inject(Api), MediaController, 0)
decorate(inject(RoomStore), MediaController, 1)
decorate(inject(DocumentStore), MediaController, 2)

export { MediaController }
